import { PaymentFlowContext, PaymentFlowResult } from '@/Services/PaymentMethod/types';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import AppConfig from '@/AppConfig';
import store from '@/store';
import { redirectToPaymentComplete } from '@/Services/PaymentMethod/processPayment';
import { mapTaxLocationValidationError } from '@/Services/PaymentMethod/taxLocationError';

export default async function processBankTransferPayment(context: PaymentFlowContext): Promise<PaymentFlowResult> {
  try {
    // Save payment method (creates customer but no payment method in Stripe)
    try {
      const saveResult = await axios.post(AppConfig.getAPIBaseUrl() + '/subscription/stripe/payment-methods', {
        payment_method_id: null,
        legal_entity: context.stripeCheckoutService.legalEntity,
        customer: context.stripeCheckoutService.customer,
      });

      if (saveResult?.status !== 200) {
        return {
          success: false,
          error: {
            message_key: 'checkout.form.invalid',
            container: 'checkoutBillingAddressContainer',
          },
        };
      }
    } catch (error) {
      // Bad billing address rejected by Stripe Tax validation — a user-fixable 422, not an exception.
      const taxLocationError = mapTaxLocationValidationError(error);
      if (taxLocationError) {
        return { success: false, error: taxLocationError };
      }

      Sentry.captureException(error);
      return {
        success: false,
        error: {
          message_key: 'checkout.form.invalid',
          container: 'checkoutBillingAddressContainer',
        },
      };
    }

    // Create subscription with collection_method: send_invoice
    try {
      const stripeSubService = context.stripeSubscriptionService;
      const isUpgrade = !!stripeSubService.subscription?.id;

      const response = await axios({
        method: isUpgrade ? 'patch' : 'post',
        url: `${AppConfig.getAPIBaseUrl()}/subscription/stripe${isUpgrade ? `/${context.selectedPlan.id}` : ''}`,
        data: {
          plan_id: context.selectedPlan.id,
          payment_method_type: 'send_invoice',
        },
      });

      // Save subscription to Vuex store
      if (response?.data) {
        await store.dispatch('subscription/setActiveSubscription', response.data);
      }
    } catch (error) {
      Sentry.captureException(error);

      return {
        success: false,
        error: {
          message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
          container: 'payment-element-container',
          error_code: 'ERR-100',
        },
      };
    }

    await redirectToPaymentComplete(context);

    return { success: true };
  } catch (error: any) {
    Sentry.captureException(error);
    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-101',
      },
    };
  }
}
